import React, { Component, ReactNode } from "react";

interface ErrorBoundaryProps {
    fallback: (error: Error, errorInfo: React.ErrorInfo) => ReactNode;
    children: ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
    errorInfo: React.ErrorInfo | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    state: ErrorBoundaryState = { hasError: false, error: null, errorInfo: null };

    static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, info: React.ErrorInfo): void {
        this.setState({ errorInfo: info });
        console.debug("ErrorBoundary caught an error:", error, info);
    }

    render() {
        const { hasError, error, errorInfo } = this.state;
        if (hasError && error) {
            // errorInfo arrives via componentDidCatch slightly after getDerivedStateFromError;
            // use an empty placeholder on the first render to avoid re-rendering the broken child
            return this.props.fallback(error, errorInfo ?? { componentStack: "" });
        }
        return this.props.children;
    }
}

export default ErrorBoundary;
